/**
 * Buddy Group Domain Types
 *
 * Hand-written canonical types for the buddy group domain.
 * These match the actual backend workflow response shapes from
 * /api/buddy-group-ops endpoints — NOT the stale generated CRUD row types.
 *
 * Source of truth: backend schemas at
 *   api_backend_fastify_server/src/schemas/business/buddy-group-schemas.ts
 *   api_backend_fastify_server/src/domain/buddy-group-rules.ts
 */

// ─── Status & Enums ────────────────────────────────────────────────────────

export type BuddyGroupStatus = 'pending' | 'active' | 'locked' | 'disbanded';

export type DuplicateDetectionMode = 'card_number' | 'name_dob';

// ─── Core Domain Types ─────────────────────────────────────────────────────

/** Invitation status for members shown in the group detail view. */
export type MemberInvitationStatus = 'accepted' | 'pending';

/** Member as returned by the my-group endpoint (enriched with display info). */
export interface BuddyGroupMemberView {
  id: string;
  user_profile_id: string;
  is_primary: boolean;
  display_name: string | null;
  member_number: string | null;
  /** Member's primary email address. Null if not on file or lookup failed. */
  email?: string | null;
  /** 'accepted' for real members, 'pending' for outstanding invitations */
  invitation_status?: MemberInvitationStatus;
  /** The invitation row ID (for pending invitations — used to cancel/revoke) */
  invitation_id?: string;
}

/**
 * One member's roping entry whose partner is NOT in the buddy group.
 * Per Option A in
 * docs/decisions/buddy-group-team-roping-conflict-resolution.md the entry
 * is governed by the pair, not the group. Surfaced so the UI can render
 * the "partnered entries not governed by this group" copy on the group
 * detail page, the attach-success screen, and at invite time.
 */
export interface DecoupledPartneredEntry {
  registration_id: string;
  event_id: string;
  event_name: string | null;
  member_user_profile_id: string;
  member_name: string | null;
  partner_user_profile_id: string;
  partner_name: string | null;
}

/** Full buddy group with members — used for detail views. */
export interface BuddyGroupDetail {
  id: string;
  group_name: string | null;
  rodeo_id: string | null;
  primary_member_user_profile_id: string | null;
  status: BuddyGroupStatus;
  /** Locked rodeo-level 1st performance preference (set by primary). */
  preferences_first?: string | null;
  /** Locked rodeo-level 2nd performance preference (set by primary). */
  preferences_second?: string | null;
  /** Timestamp the preferences were committed; null = not yet set. */
  preferences_locked_at?: string | null;
  members: BuddyGroupMemberView[];
  /**
   * Roping entries owned by group members whose partner is outside the
   * group. Empty array when none, may be undefined when the response
   * predates this field. Always scoped to the rodeo this view is for.
   */
  decoupled_partnered_entries?: DecoupledPartneredEntry[];
}

/** Set-preferences payload for the rodeo-level setup form. */
export interface SetBuddyGroupPreferencesRequest {
  /** Required — preferences are scoped per (group, rodeo) attachment. */
  rodeo_id: string;
  preferences_first: string;
  preferences_second: string | null;
}

/** Set-preferences response. */
export interface SetBuddyGroupPreferencesResponse {
  buddy_group_id: string;
  rodeo_id: string;
  preferences_first: string;
  preferences_second: string | null;
  preferences_locked_at: string;
  backfilled_registration_count: number;
}

/** Edit-gating response. */
export interface PreferencesEditableResponse {
  allowed: boolean;
  reason: string | null;
}

/** Summary for list views — derived from detail or CRUD list. */
export interface BuddyGroupSummary {
  id: string;
  group_name: string | null;
  /**
   * Legacy single-rodeo column. Kept for backward compatibility with
   * pre-attachments callers that haven't been migrated. New callers
   * should prefer `attached_rodeo_ids` which is the authoritative M:N
   * list of rodeos the group is registered for.
   */
  rodeo_id: string | null;
  /**
   * Every rodeo the buddy group is currently registered for. Empty
   * means the group is not registered for any rodeo yet (a "template"
   * that just needs members + a target).
   */
  attached_rodeo_ids: string[];
  primary_member_user_profile_id: string | null;
  status: BuddyGroupStatus;
  /** Accepted-only member count (excludes pending invitations). */
  member_count: number;
  /** Pending invitation count for this group. */
  pending_invitation_count?: number;
  /** Whether the current user is the primary cardholder */
  is_primary: boolean;
  /** Rodeo name for display (joined from rodeos table) */
  rodeo_name?: string;
}

/**
 * One entry from GET /buddy-group-ops/:groupId/rodeos.
 *
 * Combines rodeo metadata (name, dates, location) with the
 * (group, rodeo) attachment row's locked performance preferences.
 * `is_upcoming` is computed server-side as `end_date >= today` (or
 * end_date null) so a rodeo currently in progress still surfaces here.
 */
export interface BuddyGroupRodeoEntry {
  rodeo_id: string;
  name: string | null;
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  timezone: string | null;
  rodeo_or_jackpot: string | null;
  preferences_first: string | null;
  preferences_second: string | null;
  preferences_locked_at: string | null;
  is_upcoming: boolean;
  /** Finals rodeos have no performance preferences — hides the edit path. */
  is_finals?: boolean;
}

/** Bare member record from join/CRUD (no display enrichment). */
export interface BuddyGroupMemberRecord {
  id: string;
  buddy_group_id: string;
  user_profile_id: string;
  is_primary: boolean;
}

// ─── Request Types ──────────────────────────────────────────────────────────

export interface CreateBuddyGroupRequest {
  rodeo_id?: string;
  /** Required, non-empty after trim. Backend rejects with `GROUP_NAME_REQUIRED`. */
  group_name: string;
}

export interface JoinBuddyGroupRequest {
  buddy_group_id: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
}

export interface ValidateBuddyGroupRequest {
  rodeo_id: string;
  buddy_group_id?: string;
}

export interface SetPrimaryRequest {
  user_profile_id: string;
}

// ─── Response Types (match backend schema exactly) ──────────────────────────

export interface CreateBuddyGroupResponse {
  buddy_group: {
    id: string;
    group_name: string;
    rodeo_id: string | null;
    primary_member_user_profile_id: string | null;
    status: string;
  };
  member: BuddyGroupMemberRecord;
}

export interface JoinBuddyGroupResponse {
  member: BuddyGroupMemberRecord;
  group_size: number;
}

export interface ValidateBuddyGroupResponse {
  valid: boolean;
  buddy_groups_enabled: boolean;
  double_entry_active: boolean;
  member_eligible: boolean;
  existing_group_id: string | null;
  errors: Array<{ code: string; message: string }>;
  settings: {
    max_buddy_group_size: number;
    member_only_buddy_groups: boolean;
    duplicate_detection_mode: string;
  };
}

export interface SetPrimaryResponse {
  success: boolean;
  buddy_group_id: string;
  primary_member_user_profile_id: string;
}

export interface MyGroupResponse {
  buddy_group: BuddyGroupDetail | null;
}

// ─── Error Types ────────────────────────────────────────────────────────────

export interface BuddyGroupError {
  error: string;
  code: string;
}

export const BuddyGroupErrorCodes = {
  BUDDY_GROUPS_DISABLED: 'BUDDY_GROUPS_DISABLED',
  DOUBLE_ENTRY_ACTIVE: 'BUDDY_GROUP_DOUBLE_ENTRY_ACTIVE',
  GROUP_FULL: 'BUDDY_GROUP_FULL',
  ALREADY_IN_GROUP: 'BUDDY_GROUP_ALREADY_IN_GROUP',
  MEMBER_ONLY: 'BUDDY_GROUP_MEMBER_ONLY',
  DUPLICATE_MEMBER: 'BUDDY_GROUP_DUPLICATE_MEMBER',
  GROUP_NOT_FOUND: 'BUDDY_GROUP_NOT_FOUND',
  NOT_GROUP_MEMBER: 'BUDDY_GROUP_NOT_MEMBER',
  INVALID_SIZE: 'BUDDY_GROUP_INVALID_SIZE',
  INVALID_PRIMARY: 'BUDDY_GROUP_INVALID_PRIMARY',
  GROUP_IMMUTABLE: 'BUDDY_GROUP_IMMUTABLE',
} as const;

// ─── Constants ──────────────────────────────────────────────────────────────

export const BUDDY_GROUP_MIN_SIZE = 2;
export const BUDDY_GROUP_MAX_SIZE = 4;
